import type { NostrMetadata } from '@nostrify/nostrify';
import { sanitizeUrl } from './sanitizeUrl';

/**
 * What the card's QR code points at. Defaults to the profile's own website,
 * falling back to their ditto.pub profile when they haven't set one.
 */
export type QrTargetKind = 'website' | 'profile' | 'custom';

export interface QrTarget {
  kind: QrTargetKind;
  /** Free-form URL, only used when `kind` is `'custom'`. */
  custom: string;
}

export const DEFAULT_QR_TARGET: QrTarget = { kind: 'website', custom: '' };

/**
 * A NIP-05 identifier reduced to the handle Ditto puts in a profile URL:
 * `_@example.com` is the domain's root identity and drops its `_@`, everything
 * else keeps `user@domain`. Returns `undefined` for anything that isn't a
 * plausible `local@domain` pair, so a malformed field can't produce a URL that
 * resolves to nothing.
 */
export function nip05Handle(nip05: string | undefined): string | undefined {
  const value = nip05?.trim().toLowerCase();
  if (!value) return undefined;
  if (!/^[a-z0-9\-_.]+@[a-z0-9\-.]+\.[a-z]{2,}$/.test(value)) return undefined;
  return value.replace(/^_@/, '');
}

/**
 * The profile's page on ditto.pub. A verified NIP-05 makes for a far friendlier
 * printed address than 63 characters of bech32 — and the caption under the QR
 * is meant to be read — so the handle wins whenever the profile has one.
 */
export function dittoProfileUrl(npub: string, nip05?: string): string {
  const handle = nip05Handle(nip05);
  return handle ? `https://ditto.pub/@${handle}` : `https://ditto.pub/${npub}`;
}

/**
 * Resolve the QR target to a real https URL. A target that can't be honored
 * (no website on the profile, an empty or non-https custom URL) falls back to
 * the ditto.pub profile so the code always scans to something useful.
 */
export function resolveQrUrl(
  target: QrTarget,
  opts: { website?: string; npub: string; nip05?: string },
): string {
  const profile = dittoProfileUrl(opts.npub, opts.nip05);
  switch (target.kind) {
    case 'custom':
      return sanitizeUrl(target.custom) ?? profile;
    case 'profile':
      return profile;
    case 'website':
    default:
      return sanitizeUrl(opts.website) ?? profile;
  }
}

/** Strip the protocol and any trailing slash — printed URLs read better. */
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

export interface ProfileLink {
  /** Stable identity: `field:<n>`, the kind-0 key, or `custom:<n>`. */
  id: string;
  /** The field's own label, e.g. "Treasures" or "$BTC". */
  label: string;
  /** Sanitized https URL, when the value is one. */
  url?: string;
  /** The value is an on-chain Bitcoin address, so it gets the ₿ mark. */
  bitcoin?: boolean;
  /** What gets printed on the card. */
  text: string;
}

/**
 * Recognize an on-chain Bitcoin address — bech32 (`bc1…`, including taproot)
 * and legacy base58 (`1…` / `3…`) — with or without a `bitcoin:` URI prefix.
 * Used only to pick an icon, so a false negative just means a generic mark.
 */
export function isBitcoinAddress(value: string): boolean {
  const addr = value.trim().replace(/^bitcoin:/i, '').split('?')[0];
  return /^bc1[02-9ac-hj-np-z]{6,87}$/i.test(addr)
    || /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(addr);
}

/**
 * Kind-0 keys this app already renders, or that are never a printable link, so
 * they are excluded from the loose top-level sweep below.
 */
const HANDLED_FIELDS = new Set([
  'name',
  'display_name',
  'displayName',
  'about',
  'picture',
  'image',
  'banner',
  'website',
  'nip05',
  'lud06',
  'lud16',
  'bot',
  'shape',
  'birthday',
  // Handled separately, and an array rather than a string anyway.
  'fields',
]);

/** Turn `website_2` / `websiteTwo` into a readable "Website 2". */
function humanizeKey(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Ditto (and other clients) store a profile's extra links in a `fields` array
 * of `[label, value]` pairs. Values are usually URLs, but not always — a
 * `["$BTC", "bc1…"]` row is just as legitimate, so non-URL values are kept and
 * printed alongside their label.
 */
function customFieldLinks(metadata: NostrMetadata): ProfileLink[] {
  const raw = (metadata as Record<string, unknown>).fields;
  if (!Array.isArray(raw)) return [];

  const links: ProfileLink[] = [];
  raw.forEach((entry, i) => {
    if (!Array.isArray(entry)) return;
    const [label, value] = entry;
    if (typeof value !== 'string' || !value.trim()) return;
    const url = sanitizeUrl(value);
    const name = typeof label === 'string' && label.trim() ? label.trim() : `Link ${i + 1}`;
    links.push({
      id: `field:${i}`,
      label: name,
      url,
      bitcoin: !url && isBitcoinAddress(value),
      text: url ? displayUrl(url) : value.trim(),
    });
  });
  return links;
}

/**
 * Every extra link a profile carries: the `fields` pairs first, then any other
 * top-level key whose value happens to be a usable https URL (some clients use
 * `github`, `blog`, `website2`, … instead of `fields`).
 */
export function extraProfileLinks(metadata: NostrMetadata | undefined): ProfileLink[] {
  if (!metadata) return [];

  const links = customFieldLinks(metadata);
  for (const [key, value] of Object.entries(metadata as Record<string, unknown>)) {
    if (HANDLED_FIELDS.has(key)) continue;
    if (typeof value !== 'string') continue;
    const url = sanitizeUrl(value);
    if (!url) continue;
    links.push({ id: key, label: humanizeKey(key), url, text: displayUrl(url) });
  }
  return links;
}

/** A link the user added by hand in the card editor. */
export interface CustomLink {
  id: string;
  url: string;
}

/** Drop rows that are blank or not safe https URLs, and normalize the rest. */
export function resolveCustomLinks(rows: CustomLink[]): ProfileLink[] {
  const links: ProfileLink[] = [];
  for (const row of rows) {
    const url = sanitizeUrl(row.url);
    if (!url) continue;
    links.push({ id: row.id, label: displayUrl(url), url, text: displayUrl(url) });
  }
  return links;
}
